/*
 * The suggestion envelope: the shape every prefilled value travels in, from
 * here to POST /api/tickets, which records what the user did with it.
 *
 * Today a substring produces this — the subject becomes the title, the latest
 * message in the thread becomes the description — and every confidence is 1.
 * Tomorrow a model produces it and the confidence is the model's own. The
 * shape does not change; that is the entire reason it exists this early.
 *
 * The mirror of this file is internal/suggest/suggest.go. Both are checked
 * against contract/suggestion.example.json so they cannot drift apart.
 */

/**
 * Field names used by the ticket action. Part of the wire contract: the
 * correction log is keyed by them, so renaming one silently breaks the
 * comparison between what was proposed and what was sent.
 */
export const FIELD_TITLE = "title";
export const FIELD_DESCRIPTION = "description";
export const FIELD_PROJECT_ID = "projectId";

/**
 * Sources a deterministic prefill can cite. When a model produces the envelope
 * these are replaced by the verbatim quote backing the value, so treat
 * `source` as free text and never switch on it exhaustively.
 */
export const SOURCE_SUBJECT = "subject";
export const SOURCE_BODY = "body";

/**
 * The confidence of a value copied out of the email. It is 1 because a
 * substring is not a guess: the subject *is* what the subject said.
 */
export const CERTAIN = 1;

/**
 * One proposed value.
 *
 * `value` is nullable because "the email did not say" and "the email said
 * nothing" are different facts, and only the first may be filled in later
 * without contradicting the user. A missing field stays null, never invented.
 */
export interface SuggestedField {
  value: string | null;
  confidence: number;
  source: string | null;
}

/**
 * What the email was, independent of what was extracted from it. Captured in
 * full even though today only the subject and body are used: `receivedAt` in
 * particular is the only thing that can ever resolve a "yesterday" written in
 * a body, and it cannot be recovered after the fact.
 */
export interface MessageContext {
  /**
   * Office's `internetMessageId`, which is stable across mailboxes and
   * folders — unlike `itemId`, which changes when a message is moved and so
   * cannot key a duplicate check.
   */
  messageId: string;
  /** RFC 3339, from Office's `dateTimeCreated`. */
  receivedAt: string;
  from: string;
  /** The full subject line, not the truncated title. */
  subject: string;
}

export interface Suggestion {
  fields: Record<string, SuggestedField>;
  messageContext: MessageContext;
}

/** A value read straight out of the email. */
export function proposed(value: string, source: string): SuggestedField {
  return { value, confidence: CERTAIN, source };
}

/**
 * A field this email cannot answer.
 *
 * Confidence 0 and source null say "nothing was proposed", which is what makes
 * a later `projectId` filled in by the user register as *filled* rather than
 * as a correction of something we got wrong.
 */
export function absent(): SuggestedField {
  return { value: null, confidence: 0, source: null };
}

/**
 * Confidence as the three bands the pane renders.
 *
 * Today everything is `high` or `none`. The bands exist so that the first 0.62
 * out of a model renders without touching the UI — which is the whole bet of
 * painting confidence from day one.
 */
export type ConfidenceBand = "high" | "medium" | "low";

export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= 0.85) {
    return "high";
  }
  return confidence >= 0.5 ? "medium" : "low";
}

/** Reports whether the user changed a proposed value. */
export function wasEdited(field: SuggestedField | undefined, current: string): boolean {
  if (!field || field.value === null) {
    return false;
  }
  return field.value !== current;
}
